import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE;

function emptyLevel(idx) {
  const presets = [
    { level_name: 'Nivel 1: > 5000 unidades', threshold_units: 5000, threshold_value: null, discount_percent: 3, active: 1, sort_order: 1 },
    { level_name: 'Nivel 2: > 20000 unidades', threshold_units: 20000, threshold_value: null, discount_percent: 5, active: 1, sort_order: 2 },
    { level_name: 'Nivel 3: > Q30000', threshold_units: null, threshold_value: 30000, discount_percent: 7, active: 1, sort_order: 3 },
  ];
  return presets[idx] || { level_name: 'Nivel', threshold_units: null, threshold_value: null, discount_percent: 0, active: 1, sort_order: idx + 1 };
}

const Configuraciones = () => {
  const [levels, setLevels] = useState([emptyLevel(0), emptyLevel(1), emptyLevel(2)]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchAdminLevels = async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await fetch(`${API_BASE}/api/configuraciones/admin/volume-discounts`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (j?.ok && Array.isArray(j.data) && j.data.length) {
        setLevels(j.data.map((x, i) => ({
          id: x.id,
          level_name: x.level_name || emptyLevel(i).level_name,
          threshold_units: x.threshold_units != null ? Number(x.threshold_units) : null,
          threshold_value: x.threshold_value != null ? Number(x.threshold_value) : null,
          discount_percent: Number(x.discount_percent || 0),
          active: x.active ? 1 : 0,
          sort_order: Number(x.sort_order || (i + 1)),
        })));
      } else {
        // Si no hay, mantener presets por defecto
      }
    } catch (e) {
      setMsg('No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdminLevels(); }, []);

  const onChange = (idx, prop, value) => {
    setLevels((prev) => prev.map((lv, i) => (i === idx ? { ...lv, [prop]: value } : lv)));
  };

  const onSave = async () => {
    setLoading(true);
    setMsg('');
    try {
      const payload = { levels: levels.map((lv, i) => ({
        id: lv.id,
        level_name: lv.level_name || emptyLevel(i).level_name,
        threshold_units: lv.threshold_units != null && lv.threshold_units !== '' ? Number(lv.threshold_units) : null,
        threshold_value: lv.threshold_value != null && lv.threshold_value !== '' ? Number(lv.threshold_value) : null,
        discount_percent: Number(lv.discount_percent || 0),
        active: lv.active ? 1 : 0,
        sort_order: Number(lv.sort_order || (i + 1)),
      })) };
      const r = await fetch(`${API_BASE}/api/configuraciones/admin/volume-discounts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!j?.ok) throw new Error(j?.msg || 'Error al guardar');
      setMsg('Guardado ✅');
      fetchAdminLevels();
    } catch (e) {
      setMsg(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const onSeed = async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await fetch(`${API_BASE}/api/configuraciones/admin/volume-discounts/seed`, {
        method: 'POST', credentials: 'include'
      });
      const j = await r.json().catch(() => ({}));
      if (!j?.ok && j?.msg !== 'Ya existen niveles') throw new Error(j?.msg || 'No se pudo sembrar');
      setMsg('Valores predeterminados cargados');
      fetchAdminLevels();
    } catch (e) {
      setMsg(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className='text-5xl font-light mb-4'>Configuraciones</h1>
      <div className='flex justify-center mt-6'>
        <div className='w-full max-w-3xl bg-gray-50 p-6 border rounded shadow'>
          <h2 className='text-xl font-bold mb-3'>Descuentos por volumen</h2>

          <p className='text-sm text-gray-600 mb-4'>
            Define los niveles de descuento que se aplican automáticamente según la cantidad total de unidades o el valor neto de la compra. Puedes activar/desactivar cada nivel y ajustar sus umbrales y porcentaje de descuento.
          </p>

          {levels.map((lv, idx) => (
            <div key={idx} className='mb-4 p-3 border rounded bg-white'>
              <div className='flex flex-wrap gap-3 items-end'>
                <div className='flex-1 min-w-[220px]'>
                  <label className='block text-gray-700 text-sm font-semibold mb-1'>Nombre del nivel</label>
                  <input
                    value={lv.level_name}
                    onChange={(e) => onChange(idx, 'level_name', e.target.value)}
                    className='w-full border rounded px-3 py-2'
                  />
                </div>
                <div className='w-[160px]'>
                  <label className='block text-gray-700 text-sm font-semibold mb-1'>Umbral (unidades)</label>
                  <input
                    type='number'
                    value={lv.threshold_units ?? ''}
                    onChange={(e) => onChange(idx, 'threshold_units', e.target.value === '' ? null : Number(e.target.value))}
                    className='w-full border rounded px-3 py-2'
                    placeholder='e.g. 5000'
                  />
                </div>
                <div className='w-[160px]'>
                  <label className='block text-gray-700 text-sm font-semibold mb-1'>Umbral (Q)</label>
                  <input
                    type='number'
                    value={lv.threshold_value ?? ''}
                    onChange={(e) => onChange(idx, 'threshold_value', e.target.value === '' ? null : Number(e.target.value))}
                    className='w-full border rounded px-3 py-2'
                    placeholder='e.g. 30000'
                  />
                </div>
                <div className='w-[140px]'>
                  <label className='block text-gray-700 text-sm font-semibold mb-1'>Descuento (%)</label>
                  <input
                    type='number'
                    value={lv.discount_percent}
                    onChange={(e) => onChange(idx, 'discount_percent', Number(e.target.value || 0))}
                    className='w-full border rounded px-3 py-2'
                    step='0.01'
                    min='0'
                  />
                </div>
                <div className='w-[120px]'>
                  <label className='block text-gray-700 text-sm font-semibold mb-1'>Activo</label>
                  <select
                    value={lv.active ? 1 : 0}
                    onChange={(e) => onChange(idx, 'active', Number(e.target.value))}
                    className='w-full border rounded px-3 py-2 bg-white'
                  >
                    <option value={1}>Sí</option>
                    <option value={0}>No</option>
                  </select>
                </div>
                <div className='w-[120px]'>
                  <label className='block text-gray-700 text-sm font-semibold mb-1'>Orden</label>
                  <input
                    type='number'
                    value={lv.sort_order}
                    onChange={(e) => onChange(idx, 'sort_order', Number(e.target.value || 1))}
                    className='w-full border rounded px-3 py-2'
                    min='1'
                  />
                </div>
              </div>
            </div>
          ))}

          {msg && <div className='mb-3 text-sm text-gray-700'>{msg}</div>}

          <div className='flex gap-3'>
            <button
              className='bg-blue-700 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50'
              onClick={onSave}
              disabled={loading}
            >
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
            <button
              className='bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded disabled:opacity-50'
              onClick={onSeed}
              disabled={loading}
            >
              Cargar predeterminados
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Configuraciones;
